import { useState, useEffect, useRef, useCallback } from "react";
import { X, Search, Check } from "lucide-react";
import PlayerAvatar from "./PlayerAvatar";
import { useFb } from "../../utils/football";
import { hapticLight, hapticSuccess } from "../../utils/haptics";

const SkeletonRow = () => (
  <div className="my-1.5 h-[60px] animate-pulse rounded-xl bg-secondary/60" />
);

/**
 * PlayerPickerModal — bottom-sheet picker wired to GET /api/football/players.
 * Supports add mode (empty slot) and swap mode (current player at top, tap to remove).
 * Infinite scroll, search, credit filters, team filter.
 */
export const PlayerPickerModal = ({
  open, onClose, position, mode = "add", currentPlayer = null,
  pickedIds = new Set(), budgetRemaining = 100, teams = [], onPick, onRemove,
}) => {
  const fb = useFb();
  const [players, setPlayers] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [creditFilter, setCreditFilter] = useState("within"); // within | 7 | all
  const [teamId, setTeamId] = useState("");
  const listRef = useRef(null);
  const openedAt = useRef(0);
  const inputRef = useRef(null);
  const sheetRef = useRef(null);

  // Mount-guard: record open time so the opening touch can't leak to the backdrop.
  useEffect(() => {
    if (open) openedAt.current = Date.now();
  }, [open]);

  // iOS keyboard fix: the on-screen keyboard shrinks visualViewport (not the layout
  // viewport), so the sheet would otherwise stay full-height and hide the input/list.
  // Clamp the sheet's max-height to the visible viewport whenever the keyboard toggles.
  useEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    const node = sheetRef.current;
    if (!vv) return;
    const apply = () => { if (node) node.style.maxHeight = `${Math.round(vv.height * 0.92)}px`; };
    apply();
    vv.addEventListener("resize", apply);
    return () => { vv.removeEventListener("resize", apply); if (node) node.style.maxHeight = ""; };
  }, [open]);

  const onSearchFocus = () => {
    // Scroll the input into the visible area once the keyboard animation settles.
    setTimeout(() => inputRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }), 150);
  };

  const effectiveBudget = budgetRemaining + (mode === "swap" && currentPlayer ? currentPlayer.credits || 0 : 0);

  const maxCredits = creditFilter === "within" ? effectiveBudget : creditFilter === "7" ? 7 : undefined;

  const fetchPage = useCallback(async (nextOffset, replace) => {
    setLoading(true);
    try {
      const params = { position, sort: "credits_desc", limit: 30, offset: nextOffset };
      if (search) params.search = search;
      if (teamId) params.team_id = teamId;
      if (maxCredits !== undefined) params.max_credits = maxCredits;
      const data = await fb.get("/football/players", params);
      const incoming = (data.players || []).map((p) => ({ ...p, team_name: p.team?.name, player_id: p.id }));
      setPlayers((prev) => (replace ? incoming : [...prev, ...incoming]));
      setHasMore(data.has_more);
      setOffset(nextOffset + incoming.length);
    } catch (e) {
      setHasMore(false);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, search, teamId, maxCredits]);

  // Reset + fetch on filter/search change while open
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { setPlayers([]); setOffset(0); fetchPage(0, true); }, search ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, position, search, teamId, creditFilter]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el || loading || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) fetchPage(offset, false);
  };

  if (!open) return null;

  const filters = [
    { key: "within", label: "Within budget" },
    { key: "7", label: "≤ 7Cr" },
    { key: "all", label: "All" },
  ];

  return (
    <div className="football-theme fixed inset-0 z-[60] flex flex-col justify-end bg-black/60" data-testid="player-picker-modal">
      <button className="absolute inset-0" onClick={() => { const dt = Date.now() - openedAt.current; if (dt > 280) onClose(); }} aria-label="close" data-testid="picker-backdrop" />
      <div ref={sheetRef} className="relative flex max-h-[88dvh] flex-col rounded-t-2xl border-t border-border bg-card">
        <div className="mx-auto mt-2.5 h-1 w-10 rounded-full bg-slate-600" />
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full bg-secondary" data-testid="picker-close">
            <X size={18} />
          </button>
          <span className="text-base font-extrabold">Pick {position}</span>
          <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1.5 text-[13px] font-extrabold wc-gold" data-testid="picker-budget">
            {effectiveBudget.toFixed(1)}Cr
          </span>
        </div>

        {/* Swap: current player at top */}
        {mode === "swap" && currentPlayer && (
          <button
            onClick={() => { hapticLight(); onRemove(currentPlayer); }}
            className="mx-4 mt-3 flex items-center gap-3 rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2.5 text-left"
            data-testid="picker-remove-current"
          >
            <PlayerAvatar player={currentPlayer} size={40} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">
                {currentPlayer.name}
                {currentPlayer.team_name && (
                  <span className="font-normal text-muted-foreground"> — {currentPlayer.team_name}</span>
                )}
              </div>
              <div className="text-xs text-muted-foreground">Currently selected · tap to remove</div>
            </div>
            <X size={18} className="text-red-400" />
          </button>
        )}

        {/* Search */}
        <div className="relative px-4 pt-3">
          <Search size={16} className="absolute left-7 top-[22px] text-muted-foreground" />
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={onSearchFocus}
            placeholder="Search players…"
            className="h-11 w-full rounded-xl border border-border bg-background pl-10 pr-3 text-sm outline-none focus:border-amber-400"
            data-testid="picker-search"
          />
        </div>

        {/* Filters + team */}
        <div className="flex gap-2 px-4 py-3">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => { hapticLight(); setCreditFilter(f.key); }}
              className={`flex-1 rounded-lg py-2 text-[13px] font-bold transition-colors ${
                creditFilter === f.key ? "wc-bg-gold text-slate-900" : "bg-secondary text-foreground"
              }`}
              data-testid={`picker-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
          <select
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            className="rounded-lg border border-border bg-secondary px-2 text-[13px] font-bold outline-none"
            data-testid="picker-team-filter"
          >
            <option value="">All teams</option>
            {teams.map((t) => (
              <option key={t.api_football_team_id} value={t.api_football_team_id}>{t.name}</option>
            ))}
          </select>
        </div>

        {/* List */}
        <div ref={listRef} onScroll={onScroll} className="flex-1 overflow-y-auto px-3 pb-6" data-testid="picker-list">
          {players.map((p) => {
            const alreadyPicked = pickedIds.has(p.id) && p.id !== currentPlayer?.player_id;
            const overBudget = p.credits > effectiveBudget;
            const disabled = alreadyPicked || overBudget;
            return (
              <button
                key={p.id}
                disabled={disabled}
                onClick={() => { hapticSuccess(); onPick(p); }}
                className={`flex w-full items-center gap-3 rounded-xl px-2 py-2.5 text-left ${disabled ? "opacity-50" : "active:bg-secondary/60"}`}
                data-testid={`picker-player-${p.id}`}
              >
                <PlayerAvatar player={p} size={44} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14px] font-bold">{p.name}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-extrabold">{p.position}</span>
                    {p.team_name}
                  </div>
                </div>
                <span className={`text-sm font-extrabold ${overBudget ? "text-red-400" : "wc-gold"}`}>{(p.credits || 0).toFixed(1)}Cr</span>
                {alreadyPicked ? (
                  <span className="rounded-lg bg-emerald-400/10 px-2 py-1 text-[11px] font-extrabold text-emerald-400">In squad</span>
                ) : (
                  <span className={`grid h-9 w-9 place-items-center rounded-full text-xl font-extrabold ${disabled ? "bg-secondary text-slate-500" : "wc-bg-gold text-slate-900"}`}>
                    {mode === "swap" ? <Check size={18} /> : "+"}
                  </span>
                )}
              </button>
            );
          })}
          {loading && (<><SkeletonRow /><SkeletonRow /><SkeletonRow /></>)}
          {!loading && players.length === 0 && (
            search || teamId || creditFilter !== "all" ? (
              <div className="py-10 text-center text-sm text-muted-foreground" data-testid="picker-empty">No players match your filters</div>
            ) : (
              <div className="py-10 text-center text-sm text-muted-foreground" data-testid="picker-syncing">
                Player data is syncing — please try again in a moment.
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayerPickerModal;
